import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Languages, Plus, Trash2, Globe2, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { detectTextDirection } from '@/lib/textDirection';

interface Language { id: string; language_name: string; proficiency_level: string | null; }

interface Props {
  languages: Language[];
  profileId: string;
  onAdd: (data: Record<string, unknown>) => void;
  onUpdate: (id: string, data: Record<string, unknown>) => void;
  onDelete: (id: string) => void;
  saving: boolean;
  editLocked?: boolean;
}

const COMMON_LANGUAGES = [
  'Arabic',
  'English',
  'French',
  'Spanish',
  'German',
  'Italian',
  'Portuguese',
  'Russian',
  'Chinese (Mandarin)',
  'Chinese (Cantonese)',
  'Japanese',
  'Korean',
  'Hindi',
  'Urdu',
  'Bengali',
  'Turkish',
  'Persian (Farsi)',
  'Dutch',
  'Swedish',
  'Norwegian',
  'Danish',
  'Finnish',
  'Polish',
  'Czech',
  'Greek',
  'Hebrew',
  'Thai',
  'Vietnamese',
  'Indonesian',
  'Malay',
  'Tagalog',
  'Swahili',
  'Romanian',
  'Hungarian',
  'Ukrainian',
  'Serbian',
  'Croatian',
  'Bulgarian',
];

const proficiencyMeta: Record<string, { labelKey: string; fallback: string; color: string; bg: string; width: string }> = {
  native: { labelKey: 'seek.profile.native', fallback: 'Native', color: 'text-seek', bg: 'bg-seek', width: 'w-full' },
  fluent: { labelKey: 'seek.profile.fluent', fallback: 'Fluent', color: 'text-seek', bg: 'bg-seek', width: 'w-[85%]' },
  proficient: { labelKey: 'seek.profile.proficient', fallback: 'Proficient', color: 'text-seek', bg: 'bg-seek', width: 'w-[75%]' },
  advanced: { labelKey: 'seek.profile.advanced', fallback: 'Advanced', color: 'text-seek', bg: 'bg-seek/80', width: 'w-[70%]' },
  intermediate: { labelKey: 'seek.profile.intermediate', fallback: 'Intermediate', color: 'text-amber-600', bg: 'bg-amber-500', width: 'w-[50%]' },
  beginner: { labelKey: 'seek.profile.beginner', fallback: 'Beginner', color: 'text-muted-foreground', bg: 'bg-muted-foreground/50', width: 'w-[25%]' },
};

const normalizeProficiency = (raw: string | null): string => {
  if (!raw) return 'intermediate';
  const lower = raw.toLowerCase().trim();
  if (lower.startsWith('native') || lower.startsWith('mother')) return 'native';
  if (lower.startsWith('fluent') || lower.startsWith('full professional')) return 'fluent';
  if (lower.startsWith('proficient') || lower.startsWith('professional working')) return 'proficient';
  if (lower.startsWith('advanced') || lower.startsWith('upper')) return 'advanced';
  if (lower.startsWith('intermediate') || lower.startsWith('limited working')) return 'intermediate';
  if (lower.startsWith('beginner') || lower.startsWith('elementary') || lower.startsWith('basic')) return 'beginner';
  if (proficiencyMeta[lower]) return lower;
  return 'intermediate';
};

export function LanguagesSection({ languages, profileId, onAdd, onUpdate, onDelete, saving, editLocked }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ language_name: '', proficiency_level: 'intermediate' });
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      if ((e as CustomEvent).detail?.sectionId === 'section-languages') {
        setEditId(null);
        setForm({ language_name: '', proficiency_level: 'intermediate' });
        setOpen(true);
      }
    };

    window.addEventListener('open-section-edit', handler);
    return () => window.removeEventListener('open-section-edit', handler);
  }, []);

  const openNew = () => {
    setEditId(null);
    setForm({ language_name: '', proficiency_level: 'intermediate' });
    setOpen(true);
  };

  const openEdit = (language: Language) => {
    setEditId(language.id);
    setForm({
      language_name: language.language_name,
      proficiency_level: normalizeProficiency(language.proficiency_level),
    });
    setOpen(true);
  };

  const handleSave = () => {
    if (!form.language_name.trim()) return;

    if (editId) {
      onUpdate(editId, { ...form, candidate_profile_id: profileId });
    } else {
      onAdd({ ...form, candidate_profile_id: profileId });
    }

    setOpen(false);
  };

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="flex items-center gap-2.5 text-lg font-bold text-foreground font-[var(--font-display)]">
            <Languages className="w-5 h-5 text-seek" />
            {t('seek.profile.languages')}
          </h3>
          <Button variant="ghost" size="sm" onClick={openNew} disabled={editLocked} className="gap-1 bg-seek/10 text-seek hover:bg-seek hover:text-white">
            <Plus className="w-4 h-4" /> {t('seek.profile.add')}
          </Button>
        </div>

        {languages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="w-12 h-12 rounded-full bg-seek/10 flex items-center justify-center mb-3">
              <Globe2 className="w-5 h-5 text-seek" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">{t('seek.profile.noLanguages')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {languages.map((language) => {
              const normalized = normalizeProficiency(language.proficiency_level);
              const meta = proficiencyMeta[normalized] ?? proficiencyMeta.intermediate;

              return (
                <div
                  key={language.id}
                  className="group relative flex items-center gap-3.5 rounded-xl border bg-card p-4 hover:border-seek/30 hover:shadow-sm transition-all"
                >
                  <div className="w-10 h-10 rounded-lg bg-seek/10 flex items-center justify-center shrink-0">
                    <Globe2 className="w-4.5 h-4.5 text-seek" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground" dir={detectTextDirection(language.language_name)}>{language.language_name}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className={cn('h-full rounded-full transition-all', meta.bg, meta.width)} />
                      </div>
                      <span className={cn('text-xs font-medium capitalize shrink-0', meta.color)}>
                        {t(meta.labelKey, meta.fallback)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity absolute top-2 end-2">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(language)} disabled={editLocked}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => setDeleteTarget({ id: language.id, name: language.language_name })} disabled={editLocked}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editId ? t('seek.profile.editLanguage') : t('seek.profile.addLanguage')}</DialogTitle>
            <DialogDescription className="sr-only">
              {t('seek.profile.languageDialogDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('seek.profile.language')}</Label>
              <Select value={form.language_name} onValueChange={(value) => setForm((prev) => ({ ...prev, language_name: value }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t('seek.profile.selectLanguage')} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {COMMON_LANGUAGES.map((language) => (
                    <SelectItem key={language} value={language}>
                      {language}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('seek.profile.proficiency')}</Label>
              <Select value={form.proficiency_level} onValueChange={(value) => setForm((prev) => ({ ...prev, proficiency_level: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="native">{t('seek.profile.native')}</SelectItem>
                  <SelectItem value="fluent">{t('seek.profile.fluent')}</SelectItem>
                  <SelectItem value="proficient">{t('seek.profile.proficient')}</SelectItem>
                  <SelectItem value="advanced">{t('seek.profile.advanced')}</SelectItem>
                  <SelectItem value="intermediate">{t('seek.profile.intermediate')}</SelectItem>
                  <SelectItem value="beginner">{t('seek.profile.beginner')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} disabled={!form.language_name.trim() || saving} className="bg-seek text-white hover:bg-seek/90">{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(nextOpen) => { if (!nextOpen) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('seek.profile.deleteLanguage')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('seek.profile.deleteLanguageConfirm', { name: deleteTarget?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { if (deleteTarget) { onDelete(deleteTarget.id); setDeleteTarget(null); } }}>
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
